import {
  createUserWithEmailAndPassword,
  deleteUser,
  getAuth,
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
  updateProfile,
  GoogleAuthProvider,
  FacebookAuthProvider,
  signInWithPopup,
  getAdditionalUserInfo,
} from "firebase/auth";
import { getStorage, ref, uploadBytesResumable } from "firebase/storage";
import { app } from "../config/firebase";
import {
  logErrorInFirestore,
  setEmployerUserProfileData,
  setJobSeekerUserProfileData,
} from "./firestoreService";
import { LOG_ERROR_CODES } from "./firestoreConstants";
import {
  callAPI,
  SEND_VERIFICATION_EMAIL_ENDPOINT,
  MANAGE_AUTH,
} from "../api/callAPI";
import { asyncActionError } from "../async/asyncReducer";
import { ACCOUNT_SIGN_IN_ROUTE } from "../api/routeConstants";
import { toast } from "react-toastify";
import { closeModal, openModal } from "../common/modals/modalReducer";
import { STANDARD_MODAL } from "../common/modals/ModalManager";
import { Button } from "semantic-ui-react";
import { signOutUser } from "../../features/auth/authActions";

export const EMPLOYER_USER = "employerUser";
export const JOB_SEEKER_USER = "jobSeekerUser";

export const FIREBASE_STORAGE_COMPANY_LOGOS = "companyLogos";
export const FIREBASE_STORAGE_CV = "cvFiles";
export const FIREBASE_STORAGE_JOB_DESCRIPTIONS = "jobDescriptions";
export const FIREBASE_STORAGE_COVER_LETTERS = "coverLetters";

export const AUTH_PASSWORD_PROVIDER = "password";
export const AUTH_GOOGLE_PROVIDER = "google.com";
export const AUTH_FACEBOOK_PROVIDER = "facebook.com";

const auth = getAuth(app);

export function signInWithEmail(creds) {
  return signInWithEmailAndPassword(auth, creds.email, creds.password);
}

export function signOutFirebase() {
  return signOut(auth);
}

async function createAccount(values) {
  const email = values.email.toLowerCase();
  const name = values.firstName.trim();
  const displayName = name + " " + values.lastName.trim();
  const result = await createUserWithEmailAndPassword(
    auth,
    email,
    values.password
  );
  await updateProfile(result.user, { displayName: displayName });
  await callAPI({
    endpoint: SEND_VERIFICATION_EMAIL_ENDPOINT,
    opts: {
      body: { email, name },
    },
  });
  return result;
}

export async function registerEmployerInFirebase({
  values,
  countryCode = "",
  currencyCode = "",
  isRecruiter = false,
  isReceiveMarketing = false,
  mode,
}) {
  try {
    const result = await createAccount(values);
    return await setEmployerUserProfileData({
      user: result.user,
      company: values.company,
      firstName: values.firstName,
      lastName: values.lastName,
      phoneNumber: values.phoneNumber,
      countryCode: countryCode,
      currencyCode: currencyCode,
      isRecruiter: isRecruiter,
      isReceiveMarketing: isReceiveMarketing,
      mode: mode,
    });
  } catch (error) {
    await logErrorInFirestore({
      error: error,
      errorPage: "Sign up - employer",
      errorCode: LOG_ERROR_CODES.signUpEmployer,
    });
    throw error;
  }
}

export async function registerJobSeekerInFirebase({
  values,
  countryCode = "",
  isReceiveMarketing = false,
}) {
  try {
    const result = await createAccount(values);
    return await setJobSeekerUserProfileData({
      user: result.user,
      firstName: values.firstName,
      lastName: values.lastName,
      countryCode: countryCode,
      isReceiveMarketing: isReceiveMarketing,
    });
  } catch (error) {
    await logErrorInFirestore({
      error: error,
      errorPage: "Sign up - job seeker",
      errorCode: LOG_ERROR_CODES.signUpJobSeeker,
    });
    throw error;
  }
}

export async function socialLogin(
  dispatch,
  history,
  selectedProvider = AUTH_GOOGLE_PROVIDER,
  isMobile = false
) {
  let provider;
  const closedByUserError = "auth/popup-closed-by-user";
  const errorMessage = `${selectedProvider} sign-in unavailable, try later`;

  if (selectedProvider === AUTH_GOOGLE_PROVIDER) {
    provider = new GoogleAuthProvider();
  }

  if (selectedProvider === AUTH_FACEBOOK_PROVIDER) {
    provider = new FacebookAuthProvider();
  }

  try {
    const result = await signInWithPopup(auth, provider);
    if (!result) {
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
    const userInfo = getAdditionalUserInfo(result);

    const email = result.user.email || null;
    const isNewUser = userInfo.isNewUser;

    // TODO remove after social debugging
    await logErrorInFirestore({
      error: "ADMIN_LOG",
      isAdminLog: true,
      errorPage: "Sign up - social login",
      errorCode: LOG_ERROR_CODES.signUpSocialLogin,
      notes: {
        provider: selectedProvider,
        result: JSON.stringify(result),
        userInfo: JSON.stringify(userInfo),
        isNewUser,
        email,
      },
      isMobile,
    });

    if (!email && isNewUser) {
      await callAPI({
        endpoint: MANAGE_AUTH,
        opts: {
          body: { id: result.user.uid, task: "DELETE_AUTH_DETAILS" },
        },
      });
      history.push(ACCOUNT_SIGN_IN_ROUTE);
      dispatch(signOutUser());
      await signOutFirebase();
      dispatch(
        openModal({
          modalType: STANDARD_MODAL,
          modalProps: {
            width: isMobile ? "95%" : 436,
            padding: 30,
            component: (
              <div>
                <h3>We couldn't sign you up</h3>
                <p>
                  To sign up with Facebook, make sure you have an{" "}
                  <span className="semi-bold">email address</span> associated
                  with your Facebook account.
                </p>
                <p>Close this popup to choose a different sign-up method</p>
                <Button
                  className="overwrite-button cta-long-button"
                  type={"button"}
                  style={{ margin: "10px 0 20px 0" }}
                  content={"Close"}
                  onClick={() => dispatch(closeModal())}
                />
              </div>
            ),
          },
        })
      );
    } else if (email && isNewUser) {
      console.log(`${selectedProvider} sign-up`);
      const user = result.user;
      const name = user.displayName.split(" ");
      await setJobSeekerUserProfileData({
        user: user,
        firstName: name[0],
        lastName: name[1],
        isReceiveMarketing: true,
      });

      if (selectedProvider === AUTH_FACEBOOK_PROVIDER) {
        await callAPI({
          endpoint: SEND_VERIFICATION_EMAIL_ENDPOINT,
          opts: {
            body: { email: user.email, name: name[0] },
          },
        });
      }
    } else {
      console.log(`${selectedProvider} sign-in`);
      history.push(ACCOUNT_SIGN_IN_ROUTE);
    }
  } catch (error) {
    if (error?.code === closedByUserError) {
      return;
    }
    toast.error(errorMessage);
    await logErrorInFirestore({
      error: error.toString(),
      errorPage: "Sign up - social login",
      errorCode: LOG_ERROR_CODES.signUpSocialLogin,
      notes: { provider: selectedProvider },
      isMobile,
    });
    dispatch(
      asyncActionError({
        code: "social-login-error",
        message: error.message || error,
      })
    );
  }
}

export function updateUserPassword(creds) {
  const user = auth.currentUser;
  return updatePassword(user, creds.newPassword1);
}

export async function deleteUserFromFirebase(successCallback) {
  const user = auth.currentUser;

  try {
    await deleteUser(user).then(successCallback);
  } catch (error) {
    console.log("error", error);
    throw error;
  }
}

export function uploadToFirebaseStorage({ path, file }) {
  const storage = getStorage(app);
  const storageRef = ref(storage, path);
  return uploadBytesResumable(storageRef, file);
}

// export function deleteFileFromFirebaseStorage({ fileRef }) {
//   const storage = getStorage(app);
//   const storageRef = ref(storage, fileRef);
//   console.log(fileRef)
//   deleteObject(storageRef)
//     .then(() => {
//       // File deleted successfully
//       console.log("file deleted");
//     })
//     .catch((error) => {
//       console.log(error);
//     });
// }

// need to sort security rules for delete. store photos per employer?
// so can do folder/{employerId}/{file} and check employerId matches request id
// export function deleteFileFromFirebaseStorage({ path }) {
//   const storageRef = firebase.storage().ref(path);
//   return storageRef.delete();
// }
